import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Modal, ModalProps } from '/imports/ui/Modal';
import { Form } from '/imports/ui/Form';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { modelClass, SolarBill, solarBillAmountDue } from '/imports/api/solarBills';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { css } from '/imports/ui/css';
import { Checkbox } from '/imports/ui/Checkbox';
import { toastOnError } from '/imports/ui/utils';
import { Orgs } from '/imports/api/orgs';
import { formatCurrency, sum } from '/imports/utils';
import { sendRealtyIncomeBills } from '/imports/api/realtyIncomeBilling';

const RealtyIncomeSendBillsModalStyled = styled(Modal)`
  .modal-window {
    width: 45rem;
    max-width: 100%;
    max-height: 100%;
    .jodit-container {
      border: ${css.inputBorder};
    }
    .test {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      input {
        position: relative;
        left: -3px;
        top: -4px;
        margin-right: 5px;
      }
    }
  }
`;

interface RealtyIncomeSendBillsModalProps extends ModalProps {
  billIds: string[];
}

/** Modal for composing and sending Realty Income solar production bill emails. */
export const RealtyIncomeSendBillsModal = (props: RealtyIncomeSendBillsModalProps) => {
  const { billIds } = props;
  const user = useUser();
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [result, loading] = useMethod(modelClass.listMethod, { query: { _id: { $in: billIds } }, limit: billIds.length }, [billIds.join(',')]);
  const bills = result?.documents;
  const [subject, setSubject] = useState('Summary of your Solar Production Bills');
  const [html, setHtml] = useState('');
  const [recipients, setRecipients] = useState<string>();
  const [ccEmails, setCcEmails] = useState<string>();
  const [test, setTest] = useState(true);
  const [accessCode, setAccessCode] = useState<string>();
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!bills?.length) return;
    setHtml(defaultMessage(bills));
  }, [bills?.length]);
  useEffect(() => {
    if (!org._id) return;
    setAccessCode(org.billingAccessCode);
  }, [org?._id]);
  const send = toastOnError(async () => {
    if (!recipients) return toast.error(strings.RecipientsAreRequired());
    setSending(true);
    const num = await sendRealtyIncomeBills.call({ billIds, subject, html, recipients, ccEmails, test, accessCode });
    setSending(false);
    toast.success(`${strings.Sent()} ${num}`);
    props.onClose?.();
  });
  return (
    <RealtyIncomeSendBillsModalStyled
      {...props}
      title={`${strings.SendBills()} - ${org.name}`}
      onClose={props.onClose}
      body={
        loading || sending ?
          <LoadingFlex caption={sending ? strings.Sending() : ''} />
          :
          <div>
            <div>{strings.YouAreSendingNumBills(bills?.length || 0)}</div>
            <hr />
            <Form onSubmit={() => { }}>
              <FormField field="subject" className="checkbox test">
                <label><Checkbox id="ri-test" checked={test} onChange={(checked) => setTest(checked)} />{strings.Test()}</label>
              </FormField>
              <div className="field-group">
                <FormField field="subject">
                  <label>{strings.Subject()}</label>
                  <Input id="ri-subject" value={subject} onChange={(subject) => setSubject(subject || '')} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="html">
                  <label>{strings.Message()}</label>
                  <JoditEditor value={html} onChange={(html) => setHtml(html)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="recipients">
                  <label>{strings.Recipients()} ({strings.CommaSeparated()})</label>
                  <Input id="ri-recipients" value={recipients} onChange={(recipients) => setRecipients(recipients)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="ccEmails">
                  <label>{strings.CcEmails()} ({strings.CommaSeparated()})</label>
                  <Input id="ri-cc" value={ccEmails} onChange={(ccEmails) => setCcEmails(ccEmails)} />
                </FormField>
              </div>
              <div className="field-group">
                <FormField field="accessCode">
                  <label>{strings.AccessCode()}</label>
                  <Input id="ri-access-code" value={accessCode} onChange={(accessCode) => setAccessCode(accessCode)} />
                </FormField>
              </div>
              <div className="buttons">
                <Button id="ri-send-btn" onClick={() => send()}>{strings.SendBills()}</Button>
              </div>
            </Form>
          </div>
      }
    />
  );
};
/**
 * Builds the default HTML email body summarizing a batch of solar bills.
 * @param bills - bills included in the email
 * @returns HTML string for the message editor
 */
const defaultMessage = (bills: SolarBill[]) => /*html*/`
<div style="max-width: 600px; font-size: 11pt;">
  <p>Hello</p>
  <p>Your solar production bills are ready.</p>
  <p>
    <b>
      <div>Total Amount Due: ${formatCurrency(sum(bills.map((b) => solarBillAmountDue(b))))}</div>
      <div>Statement Date: ${bills[0]?.issuedDate}</div>
      <div>${strings.DueDate()}: ${bills[0]?.dueDate}</div>
      <div>${strings.BillingPeriod()}: ${bills[0]?.startDate} - ${bills[0]?.endDate}</div>
    </b>
  </p>
  <p>
    <a href="__LINK__">View bills here</a>
  </p>
  <p>Thank You</p>
  <p><b>Luminia Team</b></p>
  <hr />
  <div>
    <img src="https://${window.location.host}/images/logo_dark_193_79.png" style="display: block; max-width: 193px; max-height: 79px;">
  </div>
  <p>
    <div>12707 High Bluff Drive, Suite 115</div>
    <div>San Diego, CA 92130</div>
    <div>P: (858) 866-8777</div>
  </p>
  <p style="font-size: 9pt;">
    NOTICE: This communication and its attachments (if any) have been sent to you from Luminia and contain Privileged, Proprietary and/or Confidential Information. If you are not the intended recipient, you are hereby notified that any disclosure, copying, distribution, or use of the information contained herein (including any reliance thereon) is STRICTLY PROHIBITED. If you are not the intended recipient appearing in the address lines of this communication, please indicate to the sender that you have received this communication in error, and delete the copy you received.
  </p>
</div>
`;